import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import '../styles/BlogContent.css';
import { 
  getBlogPost, 
  getRecentPosts, 
  getAllCategories, 
  getRelatedPosts,
  getAllBlogPosts 
} from '../firebase/models/blogService';
import BlogSidebar from '../components/blog/BlogSidebar';

const BlogContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: var(--spacing-xl);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div``;

const PostHeader = styled.div`
  margin-bottom: var(--spacing-xl);
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: var(--spacing-md);
  color: var(--accent-blue);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const PostTitle = styled.h1`
  margin-bottom: var(--spacing-md);
`;

const PostMeta = styled.div`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: var(--spacing-md);
`;

const FeaturedImage = styled.div`
  width: 100%;
  border-radius: 8px;
  margin-bottom: var(--spacing-lg);
  max-height: 400px;
  object-fit: cover;
`;

const PostContent = styled.div`
  background-color: white;
  padding: var(--spacing-xl);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  line-height: 1.7;
  
  p {
    margin-bottom: var(--spacing-md);
  }
  
  h2, h3, h4 {
    margin-top: var(--spacing-lg);
    margin-bottom: var(--spacing-md);
  }
  
  ul, ol {
    margin-bottom: var(--spacing-md);
    padding-left: var(--spacing-lg);
  }
  
  blockquote {
    border-left: 4px solid var(--accent-blue);
    padding-left: var(--spacing-md);
    margin-left: var(--spacing-md);
    color: #555;
    font-style: italic;
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: var(--spacing-md) 0;
  }
  
  a {
    color: var(--accent-blue);
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const RelatedPostsSection = styled.div`
  margin-top: var(--spacing-xl);
`;

const RelatedPostsTitle = styled.h3`
  margin-bottom: var(--spacing-lg);
`;

const RelatedPostsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--spacing-lg);
`;

const RelatedPostCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedPostImage = styled.div`
  height: 150px;
  background-image: url(${props => props.image || '/placeholder-image.jpg'});
  background-size: cover;
  background-position: center;
`;

const RelatedPostContent = styled.div`
  padding: var(--spacing-md);
`;

const RelatedPostTitle = styled.h4`
  margin-top: 0;
  margin-bottom: var(--spacing-sm);
  
  a {
    color: inherit;
    text-decoration: none;
    
    &:hover {
      color: var(--accent-blue);
    }
  }
`;

const RelatedPostDate = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const NotFoundMessage = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  
  h2 {
    margin-bottom: var(--spacing-md);
  }
  
  p {
    margin-bottom: var(--spacing-lg);
    color: #666;
  }
`;

const Article = styled.article`
  background-color: white;
  padding: var(--spacing-xl);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: var(--spacing-xl);
`;

const CategoriesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-xs);
  margin-top: var(--spacing-sm);
`;

const CategoryTag = styled(Link)`
  display: inline-block;
  background-color: var(--accent-blue-light, #e6f7ff);
  color: var(--accent-blue);
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  text-decoration: none;
  
  &:hover {
    background-color: var(--accent-blue);
    color: white;
  }
`;

const RelatedPosts = styled.div`
  margin-top: var(--spacing-xl);
`;

const RelatedTitle = styled.h3`
  margin-bottom: var(--spacing-lg);
`;

const RelatedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--spacing-lg);
`;

const RelatedCard = styled.div`
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const RelatedImage = styled.div`
  height: 150px;
  background-size: cover;
  background-position: center;
`;

const RelatedContent = styled.div`
  padding: var(--spacing-md);
`;

const RelatedDate = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: var(--spacing-sm);
`;

const ReadMoreLink = styled(Link)`
  color: var(--accent-blue);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TocContainer = styled.div`
  position: sticky;
  top: 2rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  max-height: 60vh;
  overflow-y: auto;
`;

const TocTitle = styled.div`
  font-weight: 700;
  margin-bottom: var(--spacing-md);
  color: var(--accent-blue);
`;

const TocList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TocItem = styled.li`
  margin-bottom: 0.5rem;
  a {
    color: var(--accent-blue);
    text-decoration: none;
    font-size: 1rem;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toc, setToc] = useState([]);
  const [processedContent, setProcessedContent] = useState('');
  const contentRef = useRef(null);
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Find post by slug
        const posts = await getAllPosts();
        
        // Normalize the slug we're looking for
        const normalizedSlug = slug.toLowerCase().trim();
        
        // Try to find the post with exact or normalized slug match
        let currentPost = posts.find(p => 
          p.slug === slug || 
          (p.slug && p.slug.toLowerCase().trim() === normalizedSlug)
        );
        
        if (!currentPost) {
          // FALLBACK APPROACH: If we can't find by slug, use the first post as demo
          if (posts.length > 0) {
            currentPost = posts[0];
          } else {
            setError('Post not found');
            setLoading(false);
            return;
          }
        }
        
        // Get full post data
        const postData = await getBlogPost(currentPost.id);
        setPost(postData);
        
        // Fetch sidebar data
        const recentPostsData = await getRecentPosts(5);
        setRecentPosts(recentPostsData.filter(post => post.published));
        
        const categoriesData = await getAllCategories();
        setCategories(categoriesData);
        
        // Fetch related posts if post has categories
        if (postData.categories && postData.categories.length > 0) {
          const related = await getRelatedPosts(postData.id, postData.categories, 3);
          setRelatedPosts(related);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [slug]);
  
  // Helper function to get all posts to find by slug
  const getAllPosts = async () => {
    try {
      return await getAllBlogPosts();
    } catch (err) {
      console.error('Error fetching all posts:', err);
      return [];
    }
  };
  
  // Create markup from post content (simple version)
  const createMarkup = (content) => {
    return { __html: content };
  };
  
  // Preprocess content: inject IDs into H2s and build ToC
  useEffect(() => {
    if (!post || !post.content) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(post.content, 'text/html');
    const headings = Array.from(doc.querySelectorAll('h2'));
    const tocItems = headings.map((h, i) => {
      let id = h.id || h.textContent.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
      h.id = id;
      return { id, text: h.textContent };
    });
    setToc(tocItems);
    setProcessedContent(doc.body.innerHTML);
  }, [post]);
  
  // Smooth scroll handler
  const handleTocClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  if (loading) {
    return (
      <BlogContainer>
        <Container>
          <MainContent>
            <p>Loading post...</p>
          </MainContent>
        </Container>
      </BlogContainer>
    );
  }
  
  if (error || !post) {
    return (
      <BlogContainer>
        <Container>
          <MainContent>
            <NotFoundMessage>
              <h2>Post Not Found</h2>
              <p>Sorry, the blog post you're looking for doesn't exist or has been removed.</p>
              <BackLink to="/blog">Back to Blog</BackLink>
            </NotFoundMessage>
          </MainContent>
        </Container>
      </BlogContainer>
    );
  }
  
  return (
    <BlogContainer>
      <Container>
        <MainContent>
          <BackLink to="/blog">← Back to Blog</BackLink>
          
          {loading ? (
            <p>Loading post...</p>
          ) : error ? (
            <NotFoundMessage>
              <h2>Post Not Found</h2>
              <p>Sorry, the blog post you're looking for doesn't exist or has been removed.</p>
              <BackLink to="/blog">Back to Blog</BackLink>
            </NotFoundMessage>
          ) : (
            <>
              <Article>
                <PostHeader>
                  <PostTitle>{post.title}</PostTitle>
                  <PostMeta>
                    <span>{formatDate(post.createdAt)}</span>
                    {post.categories && post.categories.length > 0 && categories && (
                      <CategoriesList>
                        {post.categories.map(categoryId => {
                          const category = categories.find(c => c.id === categoryId);
                          return category ? (
                            <CategoryTag 
                              key={categoryId} 
                              to={`/blog/category/${category.slug || category.id}`}
                            >
                              {category.name}
                            </CategoryTag>
                          ) : null;
                        })}
                      </CategoriesList>
                    )}
                  </PostMeta>
                </PostHeader>
                
                {post.featuredImage && (
                  <FeaturedImage>
                    <img src={post.featuredImage} alt={post.title} />
                  </FeaturedImage>
                )}
                
                <div 
                  className="blog-content"
                  ref={contentRef}
                  dangerouslySetInnerHTML={{ __html: processedContent || post.content }} 
                />
              </Article>
              
              {relatedPosts.length > 0 && (
                <RelatedPosts>
                  <RelatedTitle>Related Posts</RelatedTitle>
                  <RelatedGrid>
                    {relatedPosts.map(relatedPost => (
                      <RelatedCard key={relatedPost.id}>
                        {relatedPost.featuredImage && (
                          <RelatedImage style={{ backgroundImage: `url(${relatedPost.featuredImage})` }} />
                        )}
                        <RelatedContent>
                          <RelatedPostTitle>
                            <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                          </RelatedPostTitle>
                          <RelatedDate>{formatDate(relatedPost.createdAt)}</RelatedDate>
                          <ReadMoreLink to={`/blog/${relatedPost.slug}`}>Read More</ReadMoreLink>
                        </RelatedContent>
                      </RelatedCard>
                    ))}
                  </RelatedGrid>
                </RelatedPosts>
              )}
            </>
          )}
        </MainContent>
        
        <div>
          <BlogSidebar 
            recentPosts={recentPosts} 
            categories={categories}
          />
          {toc.length > 0 && (
            <TocContainer>
              <TocTitle>On this page</TocTitle>
              <TocList>
                {toc.map(item => (
                  <TocItem key={item.id}>
                    <a href={`#${item.id}`} onClick={e => handleTocClick(e, item.id)}>{item.text}</a>
                  </TocItem>
                ))}
              </TocList>
            </TocContainer>
          )}
        </div>
      </Container>
    </BlogContainer>
  );
};

export default BlogPost; 